import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import TradeAttachmentsField from "@/components/TradeAttachmentsField";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { tradeSchema } from "@/lib/validations";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { API_URL } from "@/lib/api";
import { formatDateToYMD } from "@/lib/date";
import TradingChart from "@/components/TradingChart";

const API = API_URL;

const TIME_PERIODS = [
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "1Y", days: 365 },
  { label: "All", days: null },
];

const formatCurrencyValue = (value) =>
  Number(value).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function SummaryCard({ title, value, subtitle, icon: Icon, trend, trendValue, color, loading }) {
  return (
    <Card className="shadow-sm hover:shadow-md transition-all duration-200 border-border/50">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${color || "bg-primary/10"}`}>
          <Icon className={`h-4 w-4 ${color ? "text-white" : "text-primary"}`} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-24 mb-1" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        {subtitle && !loading && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
        {trend !== undefined && !loading && (
          <div className={`flex items-center mt-1 text-xs ${trend >= 0 ? "text-success" : "text-destructive"}`}>
            {trend >= 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
            <span>{trendValue}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SkeletonDashboard() {
  return (
    <div className="p-4 md:p-6">
      <div className="mb-8">
        <Skeleton className="h-10 w-48 mb-2" />
        <Skeleton className="h-4 w-64" />
      </div>

      {/* Summary cards skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-8 rounded-lg" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-28 mb-1" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
        <Card className="lg:col-span-2 shadow-lg">
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-56" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full rounded" />
          </CardContent>
        </Card>
        <Card className="shadow-lg">
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Form skeleton */}
      <Card className="shadow-lg">
        <CardHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-12 w-full rounded" />
              </div>
            ))}
          </div>
          <Skeleton className="h-12 w-full rounded-full" />
        </CardContent>
      </Card>
    </div>
  );
}

export default function Dashboard({ currentStrategy, mode }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [balanceData, setBalanceData] = useState(null);
  const [trades, setTrades] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("All");
  const [chartData, setChartData] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(tradeSchema),
    mode: "onBlur",
    defaultValues: {
      currency_pair: "",
      risk_amount: "",
      profit_loss: "",
      execution_date: formatDateToYMD(new Date()),
      notes: "",
    },
  });

  const loadDashboardData = useCallback(async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [balanceRes, tradesRes, statsRes] = await Promise.allSettled([
        axios.get(`${API}/strategies/${currentStrategy.id}/balance?mode=${mode}`, { headers }),
        axios.get(`${API}/trades?strategy_id=${currentStrategy.id}&mode=${mode}`, { headers }),
        axios.get(`${API}/statistics/${currentStrategy.id}?mode=${mode}`, { headers }),
      ]);

      if (balanceRes.status === "fulfilled") setBalanceData(balanceRes.value.data);
      if (tradesRes.status === "fulfilled") {
        const sortedTrades = tradesRes.value.data.sort((a, b) =>
          new Date(b.execution_date) - new Date(a.execution_date)
        );
        setTrades(sortedTrades);
      }
      if (statsRes.status === "fulfilled") setStatistics(statsRes.value.data);
    } catch (error) {
      toast.error("Error loading dashboard data");
    } finally {
      setDataLoading(false);
    }
  }, [currentStrategy.id, mode]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Build chart data from account_growth or trades
  useEffect(() => {
    if (statistics?.account_growth?.length > 0) {
      setChartData(statistics.account_growth);
    } else if (trades.length > 0) {
      // Build cumulative P/L chart from trades
      let cumulative = 0;
      const data = [...trades].reverse().map((trade) => {
        cumulative += trade.profit_loss;
        return {
          date: trade.execution_date,
          balance: cumulative,
        };
      });
      setChartData(data);
    }
  }, [statistics, trades]);

  const handleQuickTrade = async (data) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const tradeData = {
        ...data,
        strategy_id: currentStrategy.id,
        risk_amount: parseFloat(data.risk_amount),
        profit_loss: parseFloat(data.profit_loss),
        execution_date: data.execution_date,
        attachments: [],
      };

      await axios.post(`${API}/trades?mode=${mode}`, tradeData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Trade recorded successfully!");
      reset({
        currency_pair: "",
        risk_amount: "",
        profit_loss: "",
        execution_date: formatDateToYMD(new Date()),
        notes: "",
      });
      loadDashboardData();
    } catch (error) {
      toast.error("Error saving trade");
    } finally {
      setLoading(false);
    }
  };

  const filteredTrades = selectedPeriod === "All"
    ? trades
    : trades.filter((t) => {
        const tradeDate = new Date(t.execution_date);
        const now = new Date();
        const periodDays = TIME_PERIODS.find((p) => p.label === selectedPeriod)?.days;
        const cutoff = new Date(now.setDate(now.getDate() - periodDays));
        return tradeDate >= cutoff;
      });

  const recentTrades = filteredTrades.slice(0, 8);

  // Compute strategy breakdown
  const strategyBreakdown = statistics?.strategy_breakdown || [];
  const topStrategies = strategyBreakdown.slice(0, 5);

  if (dataLoading) {
    return <SkeletonDashboard />;
  }

  return (
    <div className="p-4 md:p-6 min-h-screen" data-testid="dashboard-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
              Dashboard
            </h1>
            <p className="text-muted-foreground text-sm md:text-base">
              Strategy: <span className="font-semibold text-primary">{currentStrategy.name}</span>
              <span className="mx-2">•</span>
              Currency: <span className="font-semibold text-primary">{currentStrategy.currency}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {TIME_PERIODS.map((period) => (
              <Button
                key={period.label}
                variant={selectedPeriod === period.label ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod(period.label)}
                className="rounded-full text-xs"
              >
                {period.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <SummaryCard
            title="Total P&L"
            value={
              statistics?.total_profit_loss !== undefined
                ? `${statistics.total_profit_loss >= 0 ? "+" : ""}${formatCurrencyValue(statistics.total_profit_loss)} ${currentStrategy.currency}`
                : "—"
            }
            icon={DollarSign}
            trend={statistics?.total_profit_loss}
            trendValue={`vs initial capital`}
            color={statistics?.total_profit_loss >= 0 ? "bg-success" : "bg-destructive"}
            loading={dataLoading}
          />
          <SummaryCard
            title="Win Rate"
            value={statistics?.win_rate !== undefined ? `${statistics.win_rate}%` : "—"}
            icon={Target}
            subtitle={
              statistics
                ? `${statistics.winning_trades}W / ${statistics.losing_trades}L`
                : undefined
            }
            loading={dataLoading}
          />
          <SummaryCard
            title="Total Trades"
            value={statistics?.total_trades ?? "—"}
            icon={Activity}
            subtitle={`Avg risk: ${formatCurrencyValue(statistics?.average_risk || 0)}`}
            loading={dataLoading}
          />
          <SummaryCard
            title="Best Trade"
            value={
              statistics?.best_trade !== undefined
                ? `+${formatCurrencyValue(statistics.best_trade)} ${currentStrategy.currency}`
                : "—"
            }
            icon={Zap}
            color="bg-orange-500"
            subtitle={
              statistics?.worst_trade !== undefined
                ? `Worst: ${formatCurrencyValue(statistics.worst_trade)}`
                : undefined
            }
            loading={dataLoading}
          />
        </div>

        {/* Equity Curve + Strategy Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          <Card className="lg:col-span-2 shadow-lg border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg md:text-xl">Equity Curve</CardTitle>
                  <CardDescription>Capital evolution over time</CardDescription>
                </div>
                <Badge variant="outline" className="text-xs">
                  {selectedPeriod}
                </Badge>
              </div>
            </CardHeader>
            <CardContent style={{ height: "320px" }}>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(val) => {
                        if (!val) return "";
                        const d = new Date(val);
                        return `${d.getMonth() + 1}/${d.getDate()}`;
                      }}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "0.5rem",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="balance"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorBalance)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No chart data available yet</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-lg border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Performance by Strategy</CardTitle>
              <CardDescription>Top strategies by P/L</CardDescription>
            </CardHeader>
            <CardContent>
              {topStrategies.length > 0 ? (
                <div className="space-y-3">
                  {topStrategies.map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.name || `Strategy ${i + 1}`}</p>
                        <p className="text-xs text-muted-foreground">{s.trade_count || 0} trades</p>
                      </div>
                      <div className={`text-sm font-bold ${s.profit_loss >= 0 ? "text-success" : "text-destructive"}`}>
                        {s.profit_loss >= 0 ? "+" : ""}{formatCurrencyValue(s.profit_loss || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                  <p>No strategy data yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* TradingView-style Chart */}
        <div className="mb-6">
          <TradingChart trades={trades} />
        </div>

        {/* Recent Trades + Quick Trade Entry */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          {/* Recent Trades */}
          <Card className="lg:col-span-2 shadow-lg border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Recent Trades</CardTitle>
                  <CardDescription>Last {recentTrades.length} trades</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/${mode}/trades`)}
                  className="text-primary"
                >
                  View All →
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentTrades.length > 0 ? (
                <div className="space-y-2">
                  {recentTrades.map((trade, i) => (
                    <motion.div
                      key={trade.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                          trade.profit_loss >= 0 ? "bg-success/15" : "bg-destructive/15"
                        }`}>
                          {trade.profit_loss >= 0 ? (
                            <TrendingUp className="h-4 w-4 text-success" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-destructive" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm">{trade.currency_pair}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            Risk: {trade.risk_amount} {currentStrategy.currency}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-sm ${trade.profit_loss >= 0 ? "text-success" : "text-destructive"}`}>
                          {trade.profit_loss >= 0 ? "+" : ""}{trade.profit_loss}
                        </p>
                        <p className="text-xs text-muted-foreground">{trade.execution_date}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                  <p>No trades recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Trade Entry */}
          <Card className="shadow-lg border-2 border-primary/30 overflow-hidden bg-card relative">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-orange-400 to-primary"></div>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Quick Trade</CardTitle>
                  <CardDescription className="text-xs">Record a trade</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(handleQuickTrade)} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="quick-pair" className="text-xs font-semibold">Pair</Label>
                  <Input
                    id="quick-pair"
                    placeholder="EUR/USD"
                    {...register("currency_pair")}
                    className={`h-10 text-sm ${errors.currency_pair ? "border-destructive" : ""}`}
                  />
                  {errors.currency_pair && (
                    <p className="text-xs text-destructive">{errors.currency_pair.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="quick-risk" className="text-xs font-semibold">Risk</Label>
                    <Input
                      id="quick-risk"
                      type="number"
                      step="0.01"
                      placeholder="100"
                      {...register("risk_amount")}
                      className={`h-10 text-sm ${errors.risk_amount ? "border-destructive" : ""}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="quick-pnl" className="text-xs font-semibold">P/L</Label>
                    <Input
                      id="quick-pnl"
                      type="number"
                      step="0.01"
                      placeholder="50 or -50"
                      {...register("profit_loss")}
                      className="h-10 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="quick-date" className="text-xs font-semibold">Date</Label>
                  <Input
                    id="quick-date"
                    type="date"
                    {...register("execution_date")}
                    className="h-10 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="quick-notes" className="text-xs font-semibold">Notes</Label>
                  <Textarea
                    id="quick-notes"
                    placeholder="Optional notes..."
                    {...register("notes")}
                    rows={2}
                    className="text-sm resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-10 rounded-full font-semibold text-sm bg-gradient-to-r from-primary to-orange-600 hover:from-orange-600 hover:to-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>Saving...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Activity className="h-4 w-4" />
                      <span>Save Trade</span>
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  );
}
